import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { InventoryKeypad, parseEurToCents, centsToEur } from '../../components/inventory/InventoryKeypad';
import { MANUAL_UI_VERSION } from './ManualInventoryList';
import { ArrowLeft, Plus, Minus, Trash2, CheckCircle2, RotateCcw, FileDown, Keyboard } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}` } });
const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
const eur = centsToEur;

export default function ManualInventoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counters, setCounters] = useState([]);
  const [qtyMap, setQtyMap] = useState({});
  const [area, setArea] = useState('');
  const [kpKey, setKpKey] = useState(null);   // welche Karte das Ziffernfeld füllt
  const [kpBuf, setKpBuf] = useState('');
  const inflight = useRef(0);
  const [delEntry, setDelEntry] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [me, setMe] = useState(user);

  const applyCounters = (list) => {
    const m = {}; (list || []).forEach(c => { m[c.sig_key] = c.qty; });
    setCounters(list || []); setQtyMap(m);
  };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/manual-inventory/${id}`, authHeader());
      setInv(res.data.inventory);
      setEntries(res.data.entries || []);
      setSummary(res.data.summary);
      applyCounters(res.data.counters);
      const t = await axios.get(`${API}/api/manual-inventory/templates?inv_type=${res.data.inventory.inv_type}`, authHeader());
      setTemplates((t.data || []).filter(x => x.active));
      try { const m = await axios.get(`${API}/api/auth/me`, authHeader()); setMe(m.data); } catch { /* ignore */ }
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 404) { toast.error('Kein Zugriff'); navigate('/inventur/manuell'); }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !inv) {
    return <DashboardLayout><div className="p-6 text-sm text-muted-foreground">Lädt…</div></DashboardLayout>;
  }

  const isPfand = inv.inv_type === 'pfand';
  const isDraft = inv.status === 'draft';
  const canReopen = !!(me && (me.role === 'admin' || me.role === 'superadmin' || me.manual_reopen_permission === true));

  // ── Zählliste: laufende Mengen je Position (Snapshot-Signatur), optimistisch + atomar ──
  const sigKey = (t) => isPfand
    ? `pfand|${t.name}|${t.deposit_cents}`
    : `product|${t.name}|${t.pack_size}|${t.price_cents}|${t.price_mode}|${t.price_basis}|${t.tax_rate}`;
  const getQty = (t) => qtyMap[sigKey(t)] ?? 0;

  const refreshCounters = async () => {
    try { const res = await axios.get(`${API}/api/manual-inventory/${id}`, authHeader());
      applyCounters(res.data.counters); setSummary(res.data.summary); setEntries(res.data.entries || []);
    } catch { /* ignore */ }
  };

  const tap = async (t, delta) => {
    if (!isDraft) return;
    const key = sigKey(t);
    const cur = qtyMap[key] ?? 0;
    const next = Math.max(0, cur + delta);
    if (next === cur) return;                       // bereits 0, kein Minus darunter
    setQtyMap(m => ({ ...m, [key]: Math.max(0, (m[key] ?? 0) + delta) }));  // optimistisch
    inflight.current++;
    try {
      await axios.post(`${API}/api/manual-inventory/${id}/count`,
        { template_id: t.id, op_id: uid(), delta, area: (area || '').trim() || null }, authHeader());
    } catch (e) {
      // Optimistische Menge GENAU um den angewandten (geclampten) Betrag zurücknehmen
      // → Anzeige zeigt NUR den gespeicherten (Server-)Stand
      const applied = next - cur;
      setQtyMap(m => ({ ...m, [key]: Math.max(0, (m[key] ?? 0) - applied) }));
      if (e?.response?.status === 403) {
        toast.error('Inventur in Ihrem Paket nicht mehr verfügbar – zuletzt getippte Menge NICHT gezählt');
        navigate('/inventur/manuell');
      } else if (e?.response?.status === 503) {
        toast.error('Lizenzprüfung momentan nicht möglich – NICHT gezählt, bitte erneut versuchen');
      } else {
        toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen – NICHT gezählt');
      }
    } finally {
      inflight.current--;
      if (inflight.current === 0) refreshCounters();   // Serverstand ist maßgeblich
    }
  };

  const setAbsolute = (t) => {
    const val = parseInt(kpBuf, 10);
    if (isNaN(val) || val < 0) { toast.error('Ungültige Menge'); return; }
    const delta = val - getQty(t);
    setKpKey(null); setKpBuf('');
    if (delta !== 0) tap(t, delta);
  };

  const confirmDelete = async () => {
    if (deleting || !delEntry) return;
    setDeleting(true);
    try {
      await axios.post(`${API}/api/manual-inventory/${id}/entries/${delEntry.id}/void`, { reason: 'deleted' }, authHeader());
      toast.success('Erfassung gelöscht');
      setDelEntry(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Löschen fehlgeschlagen'); }
    finally { setDeleting(false); }
  };

  const finalize = async () => {
    try { await axios.post(`${API}/api/manual-inventory/${id}/finalize`, {}, authHeader()); toast.success('Inventur abgeschlossen'); setFinalizeOpen(false); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Abschluss fehlgeschlagen'); }
  };
  const confirmReopen = async () => {
    if (reopening) return;
    setReopening(true);
    try { await axios.post(`${API}/api/manual-inventory/${id}/reopen`, {}, authHeader()); toast.success('Wieder geöffnet'); setReopenOpen(false); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Wiederöffnen fehlgeschlagen'); }
    finally { setReopening(false); }
  };

  const download = async (fmt) => {
    try {
      const res = await axios.get(`${API}/api/manual-inventory/${id}/export.${fmt}`, { ...authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `manuelle_inventur_${id.slice(0, 8)}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Export fehlgeschlagen'); }
  };

  const packLabel = (t) => `${t.pack_size}/Pack · ${eur(t.price_cents)} € ${t.price_mode === 'pack' ? '/Pack' : '/Stk'} · ${t.price_basis === 'net' ? 'netto' : 'brutto'} · ${t.tax_rate}%`;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-3 sm:px-4 py-5 pb-28 space-y-5" data-testid="manual-inventory-detail">
        {/* Kopf */}
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <button data-testid="back-btn" onClick={() => navigate('/inventur/manuell')} className="p-2 rounded-lg hover:bg-muted"><ArrowLeft className="h-5 w-5" /></button>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-foreground">{isPfand ? 'Pfand-Inventur' : 'Produkt-Inventur'}</h1>
              <p className="text-sm text-muted-foreground">
                {inv.location_name || '—'} · {inv.inventory_date} · Status: {isDraft ? 'Entwurf' : 'Abgeschlossen'}
                <span className="ml-2 text-[11px] text-muted-foreground/70" data-testid="manual-ui-version">UI {MANUAL_UI_VERSION}</span>
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="export-csv-btn" size="sm" variant="outline" className="gap-1" onClick={() => download('csv')}><FileDown className="h-4 w-4" /> CSV</Button>
            <Button data-testid="export-pdf-btn" size="sm" variant="outline" className="gap-1" onClick={() => download('pdf')}><FileDown className="h-4 w-4" /> PDF</Button>
            {isDraft && <Button data-testid="finalize-btn" size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => setFinalizeOpen(true)}><CheckCircle2 className="h-4 w-4" /> Abschließen</Button>}
            {!isDraft && canReopen && <Button data-testid="reopen-btn" size="sm" variant="outline" className="gap-1 border-amber-500 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/20" onClick={() => setReopenOpen(true)}><RotateCcw className="h-4 w-4" /> Wieder öffnen</Button>}
          </div>
        </div>

        {/* Erfassung (nur Entwurf) */}
        {isDraft && (
          <div className="space-y-3" data-testid="count-list">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-sm text-muted-foreground">Tippe direkt bei jeder Position – jeder Tap wird sofort gezählt und gespeichert.</p>
              <input data-testid="area-input" value={area} onChange={e => setArea(e.target.value)}
                placeholder="Bereich optional (z. B. Lager)" className="px-3 py-1.5 rounded-lg border border-border bg-background text-foreground text-sm w-full sm:w-auto" />
            </div>
            {templates.length === 0 ? (
              <p className="text-sm text-amber-600" data-testid="no-templates">Keine {isPfand ? 'Pfandarten' : 'Produktvorlagen'} vorhanden. Lege sie in der Übersicht unter „Vorlagen" an.</p>
            ) : (() => {
              const seen = new Set();
              const uniq = templates.filter(t => { const k = sigKey(t); if (seen.has(k)) return false; seen.add(k); return true; });
              return uniq.map(t => {
              const qty = getQty(t);
              return (
                <div key={t.id} data-testid={`count-card-${t.id}`} className="bg-card border border-border rounded-2xl p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{isPfand ? `${eur(t.deposit_cents)} € / Stück` : packLabel(t)}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <span data-testid={`count-qty-${t.id}`} className="text-2xl font-bold text-foreground tabular-nums">{qty}</span>
                      {isPfand && <p className="text-[11px] text-muted-foreground">= {eur(t.deposit_cents * qty)} €</p>}
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-5 gap-1.5">
                    <button data-testid={`minus5-${t.id}`} disabled={qty <= 0} onClick={() => tap(t, -5)} className="h-12 rounded-lg border border-border font-semibold text-foreground active:bg-muted disabled:opacity-40">−5</button>
                    <button data-testid={`minus1-${t.id}`} disabled={qty <= 0} onClick={() => tap(t, -1)} className="h-12 rounded-lg border border-border font-semibold text-foreground active:bg-muted disabled:opacity-40">−1</button>
                    <button data-testid={`keypad-open-${t.id}`} onClick={() => { setKpKey(sigKey(t)); setKpBuf(''); }} className="h-12 rounded-lg border border-border text-muted-foreground active:bg-muted flex items-center justify-center"><Keyboard className="h-5 w-5" /></button>
                    <button data-testid={`plus1-${t.id}`} onClick={() => tap(t, 1)} className="h-12 rounded-lg border border-teal-500 bg-teal-50 dark:bg-teal-950/20 text-teal-700 dark:text-teal-300 font-semibold active:bg-teal-100">+1</button>
                    <button data-testid={`plus5-${t.id}`} onClick={() => tap(t, 5)} className="h-12 rounded-lg border border-teal-500 bg-teal-50 dark:bg-teal-950/20 text-teal-700 dark:text-teal-300 font-semibold active:bg-teal-100">+5</button>
                  </div>
                  {!isPfand && (
                    <div className="mt-1.5 flex gap-1.5">
                      <button data-testid={`minuspack-${t.id}`} disabled={qty <= 0} onClick={() => tap(t, -t.pack_size)} className="flex-1 h-9 rounded-lg border border-border text-xs text-foreground active:bg-muted disabled:opacity-40">− 1 Pack ({t.pack_size})</button>
                      <button data-testid={`pluspack-${t.id}`} onClick={() => tap(t, t.pack_size)} className="flex-1 h-9 rounded-lg border border-indigo-400 text-xs text-indigo-700 dark:text-indigo-300 active:bg-indigo-50">+ 1 Pack ({t.pack_size})</button>
                    </div>
                  )}
                  {kpKey === sigKey(t) && (
                    <div className="mt-2 rounded-lg border border-teal-200 dark:border-teal-900 bg-teal-50/60 dark:bg-teal-950/20 p-2" data-testid={`keypad-wrap-${t.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-teal-700 dark:text-teal-300">Neue Menge (aktuell {qty}): <b data-testid={`keypad-buf-${t.id}`}>{kpBuf || '0'}</b></span>
                        <button data-testid={`keypad-cancel-${t.id}`} onClick={() => { setKpKey(null); setKpBuf(''); }} className="text-xs text-muted-foreground">abbrechen</button>
                      </div>
                      <InventoryKeypad kind="digits" onKey={(k) => setKpBuf(b => (b + k).replace(/^0+(?=\d)/, ''))} onBackspace={() => setKpBuf(b => b.slice(0, -1))} onClear={() => setKpBuf('')} testid={`manual-keypad-${t.id}`} />
                      <button data-testid={`keypad-set-${t.id}`} onClick={() => setAbsolute(t)} className="w-full mt-2 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium active:bg-teal-700">Auf diese Menge setzen</button>
                    </div>
                  )}
                </div>
              );
              });
            })()}
          </div>
        )}

        {/* Auswertung */}
        {summary && <SummaryBlock isPfand={isPfand} summary={summary} />}

        {/* Historie */}
        <div>
          <h2 className="text-base font-semibold text-foreground mb-2">Einzelhistorie</h2>
          <div className="space-y-1.5" data-testid="manual-history">
            {entries.length === 0 ? <p className="text-sm text-muted-foreground">{counters.length > 0 ? 'Gezählte Mengen werden oben je Position und in der Auswertung geführt.' : 'Noch keine Erfassungen.'}</p>
              : entries.map(e => (
                <div key={e.id} data-testid={`history-row-${e.id}`} className={`flex items-center justify-between gap-2 border border-border rounded-lg px-3 py-2 ${e.voided ? 'opacity-50' : ''}`}>
                  <div className="min-w-0 text-sm">
                    <p className="font-medium text-foreground truncate">
                      {e.name}{e.area_name ? <span className="text-muted-foreground"> · {e.area_name}</span> : null}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {isPfand
                        ? `${e.qty} × ${eur(e.deposit_cents)} € = ${eur(e.nominal_cents)} €`
                        : `${e.total_units} Stk${e.count_mode === 'packs' ? ` (${e.packs || 0}×${e.pack_size}+${e.loose || 0})` : ''} · netto ${eur(e.net_cents)} € · MwSt ${eur(e.tax_cents)} € · brutto ${eur(e.gross_cents)} € (${e.tax_rate}%)`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span data-testid={`history-status-${e.id}`} className={`text-[11px] px-2 py-0.5 rounded-full ${e.voided ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>
                      {e.voided ? 'gelöscht' : 'gespeichert'}
                    </span>
                    {isDraft && !e.voided && (
                      <button data-testid={`delete-row-${e.id}`} onClick={() => setDelEntry(e)} aria-label="Erfassung löschen"
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Lösch-Bestätigung */}
      {delEntry && (
        <div className="fixed inset-0 z-[10000] bg-black/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="delete-dialog">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm my-auto max-h-[90dvh] overflow-y-auto p-5 space-y-3">
            <h3 className="font-bold text-foreground">Erfassung wirklich löschen?</h3>
            <div className="rounded-lg bg-muted/50 px-3 py-2 text-sm text-muted-foreground" data-testid="delete-dialog-detail">
              <span className="font-medium text-foreground">{delEntry.name}</span><br />
              {isPfand ? `${delEntry.qty} × ${eur(delEntry.deposit_cents)} € = ${eur(delEntry.nominal_cents)} €`
                : `${delEntry.total_units} Stück · brutto ${eur(delEntry.gross_cents)} €`}
            </div>
            <p className="text-[11px] text-muted-foreground">Wird nachvollziehbar storniert (nicht endgültig gelöscht) und aus den Summen entfernt.</p>
            <div className="flex gap-2 pt-1">
              <Button data-testid="delete-cancel-btn" autoFocus variant="outline" className="flex-1" onClick={() => { if (!deleting) setDelEntry(null); }} disabled={deleting}>Nein</Button>
              <Button data-testid="delete-confirm-btn" className="flex-1 bg-red-600 hover:bg-red-700" onClick={confirmDelete} disabled={deleting}>{deleting ? 'Löscht…' : 'Ja, löschen'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Abschluss-Bestätigung */}
      {finalizeOpen && (
        <div className="fixed inset-0 z-[10000] bg-black/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="finalize-dialog">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm my-auto max-h-[90dvh] overflow-y-auto p-5 space-y-3">
            <h3 className="font-bold text-foreground">Inventur wirklich abschließen?</h3>
            <p className="text-sm text-muted-foreground">Danach ist die Inventur gesperrt. Ein Admin (oder freigeschaltete Stationsleitung) kann sie bei Bedarf wieder öffnen.</p>
            <div className="flex gap-2 pt-1">
              <Button data-testid="finalize-cancel-btn" autoFocus variant="outline" className="flex-1" onClick={() => setFinalizeOpen(false)}>Nein</Button>
              <Button data-testid="finalize-confirm-btn" className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={finalize}>Ja, abschließen</Button>
            </div>
          </div>
        </div>
      )}

      {/* Wieder-öffnen-Bestätigung */}
      {reopenOpen && (
        <div className="fixed inset-0 z-[10000] bg-black/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="reopen-dialog">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm my-auto max-h-[90dvh] overflow-y-auto p-5 space-y-3">
            <h3 className="font-bold text-foreground">Abgeschlossene Inventur wirklich zur Bearbeitung wieder öffnen?</h3>
            <p className="text-sm text-muted-foreground">Status wechselt zurück auf Entwurf. Erfassungen und Summen bleiben unverändert erhalten; danach erneut abschließbar.</p>
            <div className="flex gap-2 pt-1">
              <Button data-testid="reopen-cancel-btn" autoFocus variant="outline" className="flex-1" onClick={() => { if (!reopening) setReopenOpen(false); }} disabled={reopening}>Nein</Button>
              <Button data-testid="reopen-confirm-btn" className="flex-1 bg-amber-600 hover:bg-amber-700" onClick={confirmReopen} disabled={reopening}>{reopening ? 'Öffnet…' : 'Ja, wieder öffnen'}</Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function SummaryBlock({ isPfand, summary }) {
  const e = centsToEur;
  if (isPfand) {
    return (
      <div className="bg-card border border-border rounded-2xl p-4" data-testid="manual-summary">
        <h2 className="text-base font-semibold text-foreground mb-2">Auswertung Pfand</h2>
        <table className="hidden sm:table w-full text-sm">
          <thead><tr className="text-left text-muted-foreground"><th className="py-1 pr-2">Pfandart</th><th className="py-1 px-2">Pfand/Stück</th><th className="py-1 px-2 text-right">Menge</th><th className="py-1 pl-2 text-right">Pfandwert</th></tr></thead>
          <tbody>
            {summary.groups.map((g, i) => (
              <tr key={i} data-testid={`sum-row-${i}`} className="border-t border-border">
                <td className="py-1 pr-2 text-foreground">{g.name}</td>
                <td className="py-1 px-2">{e(g.deposit_cents)} €</td>
                <td className="py-1 px-2 text-right">{g.qty}</td>
                <td className="py-1 pl-2 text-right font-medium text-foreground">{e(g.nominal_cents)} €</td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Mobil: gestapelte Karten – keine abgeschnittenen Werte, kein horizontaler Scroll */}
        <div className="sm:hidden space-y-2" data-testid="pfand-summary-mobile">
          {summary.groups.map((g, i) => (
            <div key={i} data-testid={`sum-mrow-${i}`} className="border-t border-border pt-2 first:border-t-0 first:pt-0">
              <div className="font-medium text-foreground break-words">{g.name}</div>
              <div className="mt-1 grid grid-cols-3 gap-2 text-xs">
                <div><div className="text-muted-foreground">Pfand/Stück</div><div className="text-foreground">{e(g.deposit_cents)} €</div></div>
                <div><div className="text-muted-foreground">Menge</div><div className="text-foreground">{g.qty}</div></div>
                <div className="text-right"><div className="text-muted-foreground">Pfandwert</div><div className="font-medium text-foreground">{e(g.nominal_cents)} €</div></div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-border pt-2">
          <span className="text-sm text-muted-foreground">Pfandwert gesamt (Nominal, <b>separat – keine Steuer</b>)</span>
          <span className="text-lg font-bold text-foreground whitespace-nowrap" data-testid="pfand-total">{e(summary.total_nominal_cents)} €</span>
        </div>
      </div>
    );
  }
  const t = summary.totals;
  return (
    <div className="bg-card border border-border rounded-2xl p-4" data-testid="manual-summary">
      <h2 className="text-base font-semibold text-foreground mb-2">Auswertung Produkte</h2>
      <table className="hidden sm:table w-full text-xs sm:text-sm">
          <thead><tr className="text-left text-muted-foreground">
            <th className="py-1 pr-2">Artikel</th><th className="py-1 px-1 text-right">Stück</th><th className="py-1 px-1">%</th>
            <th className="py-1 px-1 text-right">Netto</th><th className="py-1 px-1 text-right">MwSt</th><th className="py-1 pl-1 text-right">Brutto</th>
          </tr></thead>
          <tbody>
            {summary.groups.map((g, i) => (
              <tr key={i} data-testid={`sum-row-${i}`} className="border-t border-border">
                <td className="py-1 pr-2 text-foreground">{g.name}<span className="block text-[10px] text-muted-foreground">{g.pack_size}/Pack · {e(g.price_cents)}€ {g.price_mode === 'pack' ? '/Pack' : '/Stk'} · {g.price_basis === 'net' ? 'netto' : 'brutto'}</span></td>
                <td className="py-1 px-1 text-right">{g.total_units}</td>
                <td className="py-1 px-1">{g.tax_rate}</td>
                <td className="py-1 px-1 text-right">{e(g.net_cents)}</td>
                <td className="py-1 px-1 text-right">{e(g.tax_cents)}</td>
                <td className="py-1 pl-1 text-right font-medium text-foreground">{e(g.gross_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Mobil: gestapelte Karten – keine abgeschnittenen Werte, kein horizontaler Scroll */}
        <div className="sm:hidden space-y-2" data-testid="product-summary-mobile">
          {summary.groups.map((g, i) => (
            <div key={i} data-testid={`sum-mrow-${i}`} className="border-t border-border pt-2 first:border-t-0 first:pt-0">
              <div className="font-medium text-foreground break-words">{g.name}</div>
              <div className="text-[10px] text-muted-foreground">{g.pack_size}/Pack · {e(g.price_cents)}€ {g.price_mode === 'pack' ? '/Pack' : '/Stk'} · {g.price_basis === 'net' ? 'netto' : 'brutto'} · {g.tax_rate}%</div>
              <div className="mt-1 grid grid-cols-4 gap-2 text-xs">
                <div><div className="text-muted-foreground">Stück</div><div className="text-foreground">{g.total_units}</div></div>
                <div className="text-right"><div className="text-muted-foreground">Netto</div><div className="text-foreground">{e(g.net_cents)} €</div></div>
                <div className="text-right"><div className="text-muted-foreground">MwSt</div><div className="text-foreground">{e(g.tax_cents)} €</div></div>
                <div className="text-right"><div className="text-muted-foreground">Brutto</div><div className="font-medium text-foreground">{e(g.gross_cents)} €</div></div>
              </div>
            </div>
          ))}
        </div>
      <div className="mt-3 border-t border-border pt-2 space-y-1">
        {summary.tax_totals.map(tt => (
          <div key={tt.rate} data-testid={`tax-total-${tt.rate}`} className="flex flex-col sm:flex-row sm:justify-between text-sm text-muted-foreground">
            <span>Summe {tt.rate}%</span><span>Netto {e(tt.net)} € · MwSt {e(tt.tax)} € · Brutto {e(tt.gross)} €</span>
          </div>
        ))}
        <div className="flex flex-col sm:flex-row sm:justify-between pt-1 text-foreground font-bold" data-testid="product-total">
          <span>Gesamt ({t.total_units} Stück)</span>
          <span>Netto {e(t.net_cents)} € · MwSt {e(t.tax_cents)} € · Brutto {e(t.gross_cents)} €</span>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">Rundung: vollpräzise Rechnung, je Zeile kaufmännisch auf 2 Dezimalstellen; Gesamtsummen aus den gerundeten Zeilen (identisch in UI/PDF/CSV).</p>
    </div>
  );
}
